import { type QueryClient,useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@api';
import { queryKeys } from '@constants';
import {
  KomplyanceAiGetConversationMessagesResponse,
  KomplyanceAiGetConversationsListItem,
  KomplyanceAiGetConversationsResponse,
  KomplyanceAiRenameConversationResponse,
  RenameConversation,
} from '@libs/types';
import { removeEmojis } from '@libs/utils';

// Service functions

async function getConversationsHistory(
  limit?: number,
  signal?: AbortSignal,
): Promise<KomplyanceAiGetConversationsResponse> {
  const params = new URLSearchParams();
  if (limit) params.append('limit', limit.toString());

  const url = `api/komplyance-ai/conversations-history`;

  const response = await apiClient.get<KomplyanceAiGetConversationsResponse>(url, {
    params,
    signal,
  });

  return response.data;
}

async function getConversationById(
  conversationId: string,
  signal?: AbortSignal,
): Promise<KomplyanceAiGetConversationsResponse> {
  const response = await apiClient.get<KomplyanceAiGetConversationsResponse>(
    `api/komplyance-ai/conversations-history/${conversationId}`,
    { signal },
  );

  return response.data;
}

async function getConversationMessages(
  conversationId: string,
  firstId?: string,
  limit?: number,
  signal?: AbortSignal,
): Promise<KomplyanceAiGetConversationMessagesResponse> {
  const params = new URLSearchParams();
  if (firstId) params.append('first_id', firstId);
  if (limit) params.append('limit', limit.toString());

  const response = await apiClient.get<KomplyanceAiGetConversationMessagesResponse>(
    `api/komplyance-ai/messages/${conversationId}`,
    { params, signal },
  );

  return response.data;
}

async function renameConversation(
  conversationId: string,
  data: RenameConversation,
): Promise<KomplyanceAiRenameConversationResponse> {
  const response = await apiClient.put<KomplyanceAiRenameConversationResponse>(
    `api/komplyance-ai/conversations/${conversationId}/rename`,
    data,
  );

  return response.data;
}

// Served by ChatController, not the komplyance-ai one: deleting a conversation
// also purges its attachments from object storage, which is the chat feature's
// to own — the AI module is a plain HTTP client.
async function deleteConversation(conversationId: string): Promise<void> {
  await apiClient.delete(`api/chat/conversations/${conversationId}`);
}

// React Query hooks
export const useConversationsHistory = () => {
  return useQuery<KomplyanceAiGetConversationsResponse>({
    queryKey: queryKeys.komplyanceAi.conversations,
    queryFn: ({ signal }) => getConversationsHistory(100, signal),
    select: (data) => ({
      ...data,
      data: data.data.map((conversation) => ({
        ...conversation,
        name: removeEmojis(conversation.name),
      })),
    }),
  });
};

export const useConversationById = (conversationId: string) => {
  return useQuery<KomplyanceAiGetConversationsResponse>({
    queryKey: queryKeys.komplyanceAi.conversationById(conversationId),
    queryFn: ({ signal }) => getConversationById(conversationId, signal),
    // No select transform - we need raw data in ChatPage to detect and clean emojis
  });
};

export const useConversationMessages = (
  conversationId: string,
  firstId?: string,
  limit?: number,
) => {
  return useQuery<KomplyanceAiGetConversationMessagesResponse>({
    queryKey: queryKeys.komplyanceAi.messages(conversationId, firstId, limit),
    queryFn: ({ signal }) => getConversationMessages(conversationId, firstId, limit, signal),
    enabled: conversationId?.length > 0,
  });
};

/**
 * Patch the cached conversation list and hand back the pre-patch snapshot.
 *
 * Rename and delete both mutate the same list optimistically and both have to
 * put it back on failure; keeping the snapshot/rollback pair here means the two
 * hooks cannot drift on how that is done.
 */
const patchConversationsList = async (
  queryClient: QueryClient,
  update: (conversations: KomplyanceAiGetConversationsListItem[]) => KomplyanceAiGetConversationsListItem[],
): Promise<{ previousConversations?: KomplyanceAiGetConversationsResponse }> => {
  await queryClient.cancelQueries({ queryKey: queryKeys.komplyanceAi.conversations });
  const previousConversations = queryClient.getQueryData<KomplyanceAiGetConversationsResponse>(
    queryKeys.komplyanceAi.conversations,
  );
  queryClient.setQueryData<KomplyanceAiGetConversationsResponse>(
    queryKeys.komplyanceAi.conversations,
    (old) => (old ? { ...old, data: update(old.data) } : old),
  );
  return { previousConversations };
};

const restoreConversationsList = (
  queryClient: QueryClient,
  context?: { previousConversations?: KomplyanceAiGetConversationsResponse },
) => {
  if (context?.previousConversations !== undefined) {
    queryClient.setQueryData(queryKeys.komplyanceAi.conversations, context.previousConversations);
  }
};

interface UseRenameConversationOptions {
  onSuccess?: (conversationId: string, newName: string) => void;
  onError?: (error: Error) => void;
}

export const useRenameConversation = (options?: UseRenameConversationOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, data }: { conversationId: string; data: RenameConversation }) =>
      renameConversation(conversationId, data),
    onMutate: ({ conversationId, data }) =>
      patchConversationsList(queryClient, (conversations) =>
        conversations.map((conv) =>
          conv.id === conversationId ? { ...conv, name: data.name ?? conv.name } : conv,
        ),
      ),
    onSuccess: (response, { conversationId }) => {
      // Confirm with the server-returned name (handles any server-side transformations)
      queryClient.setQueryData<KomplyanceAiGetConversationsResponse>(
        queryKeys.komplyanceAi.conversations,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((conv) =>
              conv.id === conversationId ? { ...conv, name: response.name ?? conv.name } : conv,
            ),
          };
        },
      );
      options?.onSuccess?.(conversationId, response.name);
    },
    onError: (error, _, context) => {
      restoreConversationsList(queryClient, context);
      options?.onError?.(error);
    },
  });
};

/**
 * Permanently delete a conversation.
 *
 * Drops the card from the sidebar optimistically and restores it if the request
 * fails. On success the conversation's detail and message caches are removed
 * outright rather than invalidated — they live under their own top-level keys,
 * so refreshing the list would leave them behind, and refetching them would
 * just 404.
 */
export const useDeleteConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) => deleteConversation(conversationId),
    onMutate: (conversationId) =>
      patchConversationsList(queryClient, (conversations) =>
        conversations.filter((conv) => conv.id !== conversationId),
      ),
    onSuccess: (_response, conversationId) => {
      queryClient.removeQueries({
        queryKey: queryKeys.komplyanceAi.conversationById(conversationId),
      });
      queryClient.removeQueries({
        queryKey: queryKeys.komplyanceAi.messagesByConversation(conversationId),
      });
    },
    onError: (_error, _conversationId, context) => {
      restoreConversationsList(queryClient, context);
    },
    // Both paths guess: the success path assumes nothing else changed, and the
    // error path assumes the server never committed — untrue for a request
    // that timed out client-side after the delete landed, which would put a
    // phantom card back in the sidebar. One refetch settles it either way.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.komplyanceAi.conversations });
    },
  });
};
